// useBeneAIContext — BeneAI-01.
//
// Infers the BeneAI conversation context from the current React Router
// location. Inference is intentionally cheap and best-effort:
//   * Route prefix is matched against the known dashboard surfaces.
//   * UUID segments are extracted as the ``host_id`` when present.
//   * ``page_title`` falls back to ``document.title``.
//
// Partial context is *fine* — the BE accepts a `{}` payload. This hook
// never blocks chat on a route it can't classify; the worst case is
// ``host_kind="generic"``.

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import type { BeneAIContext, BeneAIHostKind } from "./types";


const UUID_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;


export function inferBeneAIContext(pathname: string): BeneAIContext {
  const host_id = extractUuid(pathname);
  const host_kind = classifyHost(pathname);
  return {
    route: pathname,
    host_kind,
    host_id,
    page_title: getDocumentTitle(),
  };
}


function classifyHost(pathname: string): BeneAIHostKind {
  const segs = pathname.split("/").filter(Boolean);
  // ``/dashboard`` is the catch-all; specific surfaces win below.
  if (segs[0] !== "dashboard") return "generic";
  const head = segs[1];
  if (head === undefined) return "dashboard";

  // Specific entity routes: detail (``/dashboard/accounts/:id``)
  // dominates list (``/dashboard/accounts``) so the entity hook lands.
  if (head === "accounts") {
    return segs.length >= 3 ? "account" : "accounts_list";
  }
  if (head === "commitments") return "commitment";
  if (head === "commitment-tracker") return "commitment_tracker";
  if (head === "playbooks") {
    // ``/dashboard/playbooks/:id/edit`` lands the editor; plain
    // detail or list both classify as playbook so the agent can do
    // playbook-scope work without the editor's BlockNote payload.
    if (segs[3] === "edit") return "playbook_editor";
    if (segs.length >= 3) return "playbook";
    return "playbook";
  }
  if (head === "touchpoints") return "touchpoint";
  if (head === "sales-crm") return "sales_crm";
  if (head === "team-members") return "team_members";
  if (head === "settings") return "settings";
  return "dashboard";
}


function extractUuid(pathname: string): string | null {
  const match = pathname.match(UUID_RE);
  return match ? match[0] : null;
}


function getDocumentTitle(): string | undefined {
  if (typeof document === "undefined") return undefined;
  return document.title || undefined;
}


/**
 * React hook flavor — re-renders when the route changes so the FE
 * always sends fresh context with each message.
 *
 * ``document.title`` is read on a small debounce loop because pages
 * set their title asynchronously (page mount → effect → setTitle).
 * The drawer never blocks on context resolution; a stale title is
 * a better UX than a missing one.
 */
export function useBeneAIContext(): BeneAIContext {
  const location = useLocation();
  const [title, setTitle] = useState<string | undefined>(getDocumentTitle);

  useEffect(() => {
    if (typeof document === "undefined") return;
    setTitle(document.title || undefined);
    const id = window.setInterval(() => {
      setTitle(document.title || undefined);
    }, 1_500);
    return () => window.clearInterval(id);
  }, [location.pathname]);

  return {
    route: location.pathname,
    host_kind: classifyHost(location.pathname),
    host_id: extractUuid(location.pathname),
    page_title: title,
  };
}
